/**
 * SeatPreview — shared "this is exactly how your tile will look in
 * the lobby" preview. Used by:
 *
 * 1. CharacterProfileModal (the editable presence editor in the
 *    profile sheet) — was inlined into the modal at lines 138-154.
 * 2. LockerLayout's left-rail preview — was just an IconAvatar in a
 *    plain frame, ignoring seat cosmetics entirely (B.1 in
 *    game-revamp.md).
 *
 * Composing the seat classes (border / background / particle /
 * bgHue) + the title flair + the inner stack (lamp + avatar + name +
 * title + tags + status line) into one place means the Locker can
 * show seat-cosmetic changes the moment the user taps a tile,
 * without round-tripping through the server.
 */
import type { ReactNode, CSSProperties } from 'react';
import { IconAvatar, type IconValue } from './IconAvatar';
import { titleRarityClass } from '../me/titleRarity';
import type { SeatCosmetics } from '@tt/lobby';

interface Props {
  icon: IconValue;
  seatCosmetics: SeatCosmetics;
  selectedTitle: string | null | undefined;
  name: string;
  /** IconAvatar size in px. Default 64 to match the lobby seat. */
  avatarSize?: number;
  /** Status line text below the seat. Default 'live preview'. */
  statusLine?: string;
  /** Optional alt text for the avatar (defaults to `name`). */
  alt?: string;
  /** Optional extra children appended after the status line (e.g. a
   *  "ready" tag or other lobby-state chips). */
  extras?: ReactNode;
}

export function SeatPreview({
  icon, seatCosmetics, selectedTitle, name, avatarSize = 64, statusLine = 'live preview', alt, extras,
}: Props) {
  const className = [
    'seat',
    'ready',
    'presence-preview',
    titleFlairFor(selectedTitle ?? null),
    `seat-border-${seatCosmetics.border}`,
    `seat-bg-${seatCosmetics.background}`,
    `seat-particle-${seatCosmetics.particle}`,
  ].filter(Boolean).join(' ');

  const hasBgHue = seatCosmetics.bgHue !== null && seatCosmetics.bgHue !== undefined;
  const dataProps = hasBgHue ? { 'data-bg-hue': String(seatCosmetics.bgHue) } : {};
  const style = hasBgHue
    ? ({ ['--seat-bg-h' as string]: String(seatCosmetics.bgHue) } as CSSProperties)
    : undefined;

  return (
    <div className={className} {...dataProps} style={style}>
      <span className="seat-lamp" aria-hidden="true" />
      <IconAvatar icon={icon} size={avatarSize} alt={alt ?? name} />
      <div className="seat-name">{name}</div>
      {selectedTitle && selectedTitle !== 'Tavern Regular' && (
        <div className={`seat-title ${titleRarityClass(selectedTitle)}`}>{selectedTitle}</div>
      )}
      <div className="seat-tags">
        <span className="tag ready">ready</span>
      </div>
      <div className="seat-status-line">{statusLine}</div>
      {extras}
    </div>
  );
}

/**
 * Title → flair class. Mirrors the helper that previously lived in
 * CharacterProfileModal and Room.tsx — extract here so the Locker
 * preview, the profile sheet, and the lobby seat all read identical.
 */
export function titleFlairFor(title: string | null | undefined): string {
  if (!title || title === 'Tavern Regular') return '';
  if (title === 'First Winner') return 'flair-first-win';
  if (title.endsWith(' Master')) return 'flair-master';
  if (title.endsWith(' Adept')) return 'flair-adept';
  if (title.endsWith(' Apprentice')) return 'flair-apprentice';
  if (title === 'Daily Devotee' || title === 'Streaker') return 'flair-streak';
  if (title === 'Undefeated' || title === 'Table Champion' || title === '1000 Club') return 'flair-elite';
  return 'flair-flair';
}
